import { fetchReviewDictionary } from '../../features/reviews/api';
import { getReviewMenu } from '../../features/support/adminCheckApi';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronRight, LayoutGrid, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { useMetkiZapisi } from '../../features/zapis/hooks';
import { usePermissions } from '../../features/auth/usePermissions';
import { DEV_GROUP, GRUPPA_S_KALENDAREM, KLYUCH_ZHURNALA_DNYA, NAV_GROUPS, razdelRazreshen } from './menyuAdminki';
import { RazdelSelect } from './RazdelSelect';
import { useSupportCounts } from '../../features/support/hooks';
import { useKnowledgeHealth } from '../../features/learning/hooks';
import { MiniKalendarMenyu } from './MiniKalendarMenyu';
import { useRazdely } from './razdely';
import { useQuery } from '@tanstack/react-query';
import { getCertificateMenuWord } from '../../features/certificates/api';
import { getMasterMenuWord } from '../../features/s856/api';
import { getOnlaynMenuWord } from '../../features/onlayn/api';
import { usePayrollWords } from '../../features/salary/api';
import { getWorkdayWords } from '../../features/workday/api';
import { menyuPamyat, punktPodKartochkoy } from './menyuPamyat';
import './shell-polish.css';

/** Минимальный структурный тип под lucide-иконку (без привязки к версии пакета). */
interface Props {
  /** Открыта ли выезжающая шторка на мобильном (на md+ сайдбар статичный). */
  open?: boolean;
  onClose?: () => void;
  /** Подвал сайдбара (филиал/профиль/выход) — собирается в AppShell, где есть провайдеры. */
  footer?: ReactNode;
  /** `s698`: меню свёрнуто до полосы значков (только на компьютере; шторка телефона — всегда полная). */
  svernuto?: boolean;
  /** Свернуть / раскрыть. Без него переключателя нет вовсе. */
  onPereklyuchit?: () => void;
  /** Подвал свёрнутого меню: профиль и выход значками. */
  footerSvernutyy?: ReactNode;
}



export function Sidebar({ open = false, onClose, footer, svernuto = false, onPereklyuchit, footerSvernutyy }: Props) {
  const { can, isSuperAdmin, sectionHidden } = usePermissions();
  const visitReviewWords = useQuery({queryKey:['visit-reviews','dictionary',null],queryFn:()=>fetchReviewDictionary(),enabled:can('review.manage'),retry:false,staleTime:60_000});
  const masterWord=useQuery({queryKey:['master-services-menu-word'],queryFn:getMasterMenuWord,enabled:can('services.read'),staleTime:60_000});
  const onlaynWord=useQuery({queryKey:['onlayn-nastroyki-menu-word'],queryFn:getOnlaynMenuWord,enabled:can('system.settings'),staleTime:60_000});
  const certificateWord=useQuery({queryKey:['certificate-menu-word'],queryFn:getCertificateMenuWord,enabled:can('certificate.view'),staleTime:60_000});
  const payrollWords = usePayrollWords(can('salary.master.view') || can('salary.master.self'));
  const workdayWords = useQuery({queryKey:['workday-words'],queryFn:getWorkdayWords,enabled:can('shift.open')||can('shift.handover')||can('schedule.view')||can('finance.view')||can('zapis.den.view'),staleTime:60_000});
  const workdayKeys: Record<string, string> = {'workday-shift':'shift_title','workday-graph':'graph_title','workday-cash':'cash_title','workday-master':'master_title'};
  const reviewMenu = useQuery({queryKey:['admin-check-menu'],queryFn:getReviewMenu,staleTime:60_000});
  const groups = NAV_GROUPS.map(g => ({ ...g, sections: g.sections
    .filter(s => (s.key !== 'visit-reviews' || !!visitReviewWords.data?.enabled) && (s.key !== 'master-services' || !!masterWord.data)
      && (s.key !== 'onlayn-zapis' || !!onlaynWord.data)
      && (!workdayKeys[s.key] || !!workdayWords.data)
      && (s.key !== 'workday-master' || !!workdayWords.data?.has_staff))
    .map(s => ({ ...s,
      ...(workdayKeys[s.key] ? { label: workdayWords.data!.words.texts['s878.' + workdayKeys[s.key]] } : {}),
      ...(s.key === 'visit-reviews' ? { label: visitReviewWords.data!.words.title } : {}),
      ...(s.key === 'master-services' ? { label: masterWord.data!.label } : {}),
      ...(s.key === 'onlayn-zapis' ? { label: onlaynWord.data!.label } : {}),
      ...(s.key === 'finance-masters-salary' ? { label: payrollWords.data?.words?.texts['salary.master.title'] ?? s.label } : {}),
      ...(s.key === 'finance-my-master-salary' ? { label: payrollWords.data?.words?.texts['salary.master.my_salary'] ?? s.label } : {}),
      children: s.children?.filter(c => c.key !== 'certificates' || !!certificateWord.data)
        .map(c => c.key === 'certificates' ? { ...c, label: certificateWord.data!.label } : c),
    })).map(s => s.key === 'zapis-den' && reviewMenu.data?.enabled ? {...s,children:[...(s.children ?? []),{key:'admin-check',to:'/proverka-zapisi',label:reviewMenu.data.label,permission:'zapis.den.view'}]} : s),
  }));
  // Бейдж pending на столе (тот же запрос, что Пульт базы). Без права ai_memory.learn не уходит.
  const { data: health } = useKnowledgeHealth(can('ai_memory.learn'));
  const pendingBadge = health && health.pending > 0 ? health.pending : null;
  // `s737`: сколько обращений техподдержки ждёт решения владельца. Без права support.owner запрос не уходит.
  const { data: support } = useSupportCounts(can('support.owner'));
  const supportBadge = support && support.counts.decision > 0 ? support.counts.decision : null;
  const schyotchik = (key: string): number | null =>
    key === 'learning' ? pendingBadge : key === 'support' ? supportBadge : null;

  /*
   * ⛔⛔ КРАСНЫЕ МЕТКИ ПУНКТОВ «ЗАПИСИ» (`s329`, Д28, 18.09.2026, слово
   * владельца над снимком меню: «если тут что-то появилось — подсвечивать
   * красным алертом ! по каждому пункту»).
   *
   * ⛔ ЧИСЛА — С СЕРВЕРА И ЖИВЫЕ. Считать их в браузере нечем: меню не знает
   * содержимого списков, а знало бы — считало бы по выданной странице.
   * Без права на журнал запрос не уходит вовсе.
   */
  const { data: metkiZapisi } = useMetkiZapisi(can('zapis.den.view') && !sectionHidden('zapis-den'));

  /**
   * ⛔ ВЫБРАННЫЙ РАЗДЕЛ ПРИХОДИТ ИЗ ОБЩЕГО ИСТОЧНИКА (`s311`, владелец 09.09.2026).
   * Сам список разделов уехал в верхнюю панель — «так у нас и место появится
   * впихнуть навигацию и лого и не мешать это в левом меню». Пункты выбранного
   * раздела по-прежнему рисует меню, поэтому выбор нужен обоим; своё второе
   * состояние здесь однажды разошлось бы с шапкой.
   */
  const { vybrannaya: vybrannayaGruppa, vidimyy: sectionVisible, tekushchiyKey: currentKey, otkrytyyPunkt, raskryt } = useRazdely();

  const isSectionOpen = (key: string) =>
    (otkrytyyPunkt === undefined ? currentKey : otkrytyyPunkt) === key;

  /**
   * Видно ли календарик: та же проверка, что у пункта «Запись», — своё право и
   * свой ключ видимости. Считается через ту же функцию `sectionVisible`, что
   * решает про пункты меню, чтобы у двух дверей в один экран не завелось двух
   * разных ответов на вопрос «показывать ли».
   */
  const punktZapisi = NAV_GROUPS
    .find((g) => g.label === GRUPPA_S_KALENDAREM)
    ?.sections.find((s) => s.key === KLYUCH_ZHURNALA_DNYA);
  const kalendarVidno = punktZapisi !== undefined && sectionVisible(punktZapisi);

  const toggle = (key: string) => raskryt(isSectionOpen(key) ? null : key);

  /*
   * ⛔ `s713` Д-08: ПРОКРУТКА МЕНЮ ПЕРЕЖИВАЕТ ПЕРЕХОД. Меню пересоздаётся вместе с каркасом
   * на каждой странице и раньше вставало в начало: человек прокрутил длинный раздел до
   * нужного пункта, нажал — и список снова наверху. Высота запоминается при прокрутке и
   * возвращается при появлении меню; пока она не возвращена, пустое «0» её не затирает.
   */
  const spisok = useRef<HTMLElement | null>(null);
  const prokrutkaVozvrashchena = useRef(false);
  useLayoutEffect(() => {
    const el = spisok.current;
    if (el === null || prokrutkaVozvrashchena.current) return;

    const nado = menyuPamyat.prokrutka();
    if (nado <= 0) {
      prokrutkaVozvrashchena.current = true;

      return;
    }
    el.scrollTop = nado;
    // Пункты ещё не дорисованы (права грузятся) — попробуем на следующей отрисовке.
    if (Math.abs(el.scrollTop - nado) < 2) prokrutkaVozvrashchena.current = true;
  });
  const prokrutkaMenyu = {
    ref: spisok,
    onScroll: (e: { currentTarget: HTMLElement }) => {
      if (prokrutkaVozvrashchena.current) menyuPamyat.zapomnitProkrutku(e.currentTarget.scrollTop);
    },
    // Человек взялся за меню сам — дальше запоминаем то, что он накрутил.
    onWheel: () => { prokrutkaVozvrashchena.current = true; },
    onTouchMove: () => { prokrutkaVozvrashchena.current = true; },
  };

  // `s722`: пока поверх экрана открыта карточка (запись, лид), его пункт меню остаётся выбранным.
  const mesto = useLocation();
  const podKartochkoy = (to: string) => punktPodKartochkoy(to, mesto.pathname, (mesto.state as { otkuda?: unknown } | null)?.otkuda);

  const leafClass = ({ isActive }: { isActive: boolean }) =>
    [
      'block rounded-ops-sm py-2 pl-3 pr-3 text-ops-caption transition-colors duration-ops ease-ops',
      isActive
        ? 'bg-ops-sidebar-active-bg text-ops-sidebar-active-text'
        : 'text-ops-sidebar-text-muted hover:bg-white/5 hover:text-ops-sidebar-active-text',
    ].join(' ');

  return (
    <>
      {/* Затемнение под шторкой — только на мобильном при открытой шторке. */}
      <div
        aria-hidden
        onClick={onClose}
        className={[
          'fixed inset-0 z-40 bg-ops-navy-900/60 transition-opacity md:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
      />

      <aside
        className={[
          'ops-skin ops-cosmic-sidebar flex flex-col text-ops-sidebar-text',
          // Мобайл: выезжающая шторка фиксированной ширины, скрыта за левым краем.
          'fixed inset-y-0 left-0 z-50 w-64 max-w-[82vw]',
          'transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : '-translate-x-full',
          // Десктоп: статичная колонка, всегда видна; свёрнутая — полоса значков (`s698`).
          'md:static md:z-auto md:max-w-none md:translate-x-0 md:shrink-0 md:transition-none',
          svernuto ? 'md:w-[60px]' : 'md:w-ops-sidebar',
        ].join(' ')}
        data-svernuto={svernuto ? '1' : '0'}
      >
        {/*
          ⛔ СВЁРНУТОЕ МЕНЮ — ОТДЕЛЬНАЯ КОЛОНКА ЗНАЧКОВ, ТОЛЬКО НА КОМПЬЮТЕРЕ
          (`s698`). Раздел, пункты, профиль и выход остаются под рукой; название
          видно при наведении. Календарь и подпункты в полосу не помещаются —
          они в раскрытом меню, до которого один щелчок.
        */}
        {svernuto && (
          <div className="hidden min-h-0 flex-1 flex-col items-center gap-1 px-1.5 pb-2 pt-2 md:flex">
            {onPereklyuchit && (
              <button
                type="button"
                onClick={onPereklyuchit}
                aria-label="Раскрыть меню"
                title="Раскрыть меню"
                data-testid="menyu-pereklyuchatel"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-ops-md text-ops-sidebar-text-muted transition-colors hover:bg-white/5 hover:text-white"
              >
                <PanelLeftOpen size={20} />
              </button>
            )}
            {/* Выбор раздела живёт в раскрытом меню: значок раскрывает его. */}
            <button
              type="button"
              onClick={onPereklyuchit}
              aria-label={`Раздел: ${vybrannayaGruppa}. Выбрать раздел`}
              title={`Раздел: ${vybrannayaGruppa} — выбрать`}
              className="ops-razdel-select flex h-10 w-10 shrink-0 items-center justify-center rounded-ops-md text-white"
            >
              <LayoutGrid size={18} />
            </button>
            <nav className="flex min-h-0 w-full flex-1 flex-col items-center gap-1 overflow-y-auto pt-1" aria-label="Разделы">
              {groups.filter((g) => g.label === vybrannayaGruppa).flatMap((g) =>
                g.sections.filter(sectionVisible).map((section) => {
                  const Icon = section.icon;
                  const metka = schyotchik(section.key) !== null;

                  return (
                    <NavLink
                      key={section.key}
                      to={section.to}
                      onClick={() => raskryt(section.key)}
                      title={section.label}
                      aria-label={section.label}
                      className={({ isActive }) =>
                        [
                          'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-ops-md transition-colors duration-ops ease-ops',
                          isActive
                            ? 'bg-ops-sidebar-active-bg text-ops-primary'
                            : 'text-ops-sidebar-text hover:bg-white/5 hover:text-white',
                        ].join(' ')
                      }
                    >
                      <Icon size={20} />
                      {metka && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-ops-warning" />}
                    </NavLink>
                  );
                }),
              )}
            </nav>
            {footerSvernutyy}
          </div>
        )}
        {/* Полное меню: на телефоне — всегда (шторка), на компьютере — пока не свёрнуто. */}
        <div className={['flex min-h-0 flex-1 flex-col', svernuto ? 'md:hidden' : ''].join(' ')}>
        {/*
          ⛔ ЛОГО И ВЫБОР РАЗДЕЛА УЕХАЛИ В ВЕРХНЮЮ ПАНЕЛЬ (владелец, 09.09.2026,
          `s311`): «логотип и кнопку выбора разделов впихнуть в этот квадрат…
          оно не должно мешать навигации внутри текущего раздела и отвлекать от
          работы». В колонке осталось только то, что относится к ТЕКУЩЕМУ
          разделу: филиал, календарь и пункты.
        */}
        {/* ⛔ ВОЗДУХ СВЕРХУ ОБЯЗАТЕЛЕН И НА БОЛЬШОМ ЭКРАНЕ. Лого уехало в шапку, и
            колонка осталась без своего верхнего блока — подпись «Филиал»
            прижалась к самому краю и обрезалась (поймано глазами 09.09). */}
        {onPereklyuchit ? (
          <div className="hidden items-center justify-end px-3 pt-2 md:flex">
            <button
              type="button"
              onClick={onPereklyuchit}
              aria-label="Свернуть меню"
              title="Свернуть меню"
              data-testid="menyu-pereklyuchatel"
              className="flex h-8 w-8 items-center justify-center rounded-ops-md text-ops-sidebar-text-muted transition-colors hover:bg-white/5 hover:text-white"
            >
              <PanelLeftClose size={18} />
            </button>
          </div>
        ) : (
          <div className="hidden pt-3 md:block" aria-hidden />
        )}
        <div className="flex items-center justify-end px-3 pt-3 md:hidden">
          <button
            type="button"
            onClick={onClose}
            aria-label="Закрыть меню"
            className="flex h-10 w-10 items-center justify-center rounded-ops-md text-ops-sidebar-text-muted transition-colors hover:bg-white/5 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto px-3 pb-4" data-testid="menyu-spisok" {...prokrutkaMenyu}>
          {/*
            Выпадающий список глобальных разделов (О-34), ВНУТРИ выбранного —
            аккордеон оперативных пунктов. Дерево разделов ратифицировано
            владельцем 09.09.2026 (`s310`, опись `05-tasks/s310/opis-menyu.md`).

            ⛔ `s310` (владелец): выпадашка была «бледная, ничего не видно»
            (полупрозрачный bg-white/5). Стала сплошной плашкой цвета бренда с
            белым жирным текстом и своей стрелкой: appearance-none, потому что
            системная стрелка на тёмном фоне в части браузеров исчезает вовсе.
          */}
          {/*
            ⛔ ФИЛИАЛ — НАД КАЛЕНДАРЁМ, В МЕНЮ (владелец, 09.09.2026, `s311`):
            «филиал и расписание неразрывны, равно как и другие разделы, они все
            актуальны для выбранного филиала, поэтому его уводим в меню над
            календарём». В шапке журнала он стоял как поле одного экрана — а он
            общий для всех разделов, и менять его из расписания значило искать
            переключатель заново на каждом экране.

            ⛔ ЭТО ПЕРЕКЛЮЧАТЕЛЬ, А НЕ ФИЛЬТР (op-rules §0.1 W): он говорит, ЧЕЙ
            день открыт, и потому стоит первым, над календарём.
          */}
          {/* ⛔ ШИРИНА — В ТОЧНОСТИ КАК У КАЛЕНДАРЯ (владелец 09.09: «выровнять
              ширину по ширине календаря»). Тот же контейнер, что у месяца ниже:
              два разных отступа рядом читались как случайная вёрстка. */}
          {/* ⛔ ПЕРЕКЛЮЧАТЕЛЬ ФИЛИАЛА УЕХАЛ В ВЕРХНЮЮ ШАПКУ (`s402`, Д-29
              приёмки владельца 13.09.2026): он стоит рядом с переключателем
              раздела, а левая колонка начинается с календаря месяца. Двух
              переключателей филиала на экране быть не может — иначе снова
              появятся два ответа на вопрос «чей день открыт» (`s311`). */}

          {/*
            ⛔ Календарик ЗАФИКСИРОВАН строго под выпадашкой (владелец, 09.09,
            приёмка по скрину, п.2); отдельного пункта «Календарь» нет — его
            дублировал клик по любой дате (п.1). Виден внутри «Операционки».
          */}
          {/* ⛔ Плашка календаря — почти в край колонки (владелец 09.09, красные
              линии по краям сайдбара: «поле календаря намного уже!!»): -mx-2
              съедает padding самого nav, остаётся воздух 4px с каждой стороны. */}
          {/*
            ⛔ `s014` 10.09.2026 — КАЛЕНДАРИК ЗАКРЫВАЕТСЯ ТЕМ ЖЕ КЛЮЧОМ, ЧТО И
            ЖУРНАЛ ДНЯ, КУДА ОН ВЕДЁТ. До сегодня условие было одно — «выбран
            раздел Операционка», и календарь оставался на экране у любой роли,
            которой виден хоть один пункт этого раздела. Владелец 10.09: спрятал
            «Запись» у директолога — календарь остался, потому что директологу
            виден «Лиды с форм». Вторая дверь в комнату обязана иметь тот же
            замок, что и первая: правило `prv3` «спрятал = закрыл» не про пункт
            меню, а про экран.
          */}
          {/* ⛔ ВЫБОР РАЗДЕЛА — ПЕРВЫМ В КОЛОНКЕ, НАД КАЛЕНДАРЁМ (`s404`, Д-29
              хвост, владелец 13.09.2026): он занял старое место филиала. Раздел
              отвечает «что открыто», и стоять ему над содержимым колонки; филиал
              («где я сейчас») остался в верхней шапке, и двух его переключателей
              по-прежнему нет. */}
          <div className="pb-2">
            <RazdelSelect />
          </div>

          {vybrannayaGruppa === GRUPPA_S_KALENDAREM && kalendarVidno && (
            <div className="-mx-2 px-0">
              <MiniKalendarMenyu onClose={onClose} />
            </div>
          )}
          {groups.map((group) => {
            const sections = group.sections.filter(sectionVisible);
            if (sections.length === 0) return null;

            // Показывается ровно один раздел — выбранный в списке выше.
            const groupOpen = group.label === vybrannayaGruppa;
            if (!groupOpen) return null;

            const isDev = group.label === DEV_GROUP;

            return (
              <div key={group.label} className="space-y-0.5">
                {/* ⛔ Метка раздела: пункт внутри рабочий, но данных в нём пока
                    нет. Без метки пустой экран читается как поломка. */}
                {isDev && (
                  <p className="px-3 pb-1 text-ops-micro text-ops-text-tertiary">пока пусто</p>
                )}

                {sections.map((section, i) => {
                  const Icon = section.icon;
                  /*
                   * Подзаголовок принадлежности (`s310`, владелец: «настройки
                   * разделить внутри по принадлежности»). Рисуется перед ПЕРВЫМ
                   * видимым пунктом своей подгруппы: сравнение с предыдущим
                   * ВИДИМЫМ соседом, а не с соседом по данным — иначе спрятанный
                   * правами пункт уносил бы с собой и подзаголовок живых.
                   */
                  const podgruppaHeader =
                    section.podgruppa && section.podgruppa !== sections[i - 1]?.podgruppa
                      ? section.podgruppa
                      : null;
                  // Подпункт прячется тем же слоем, что и раздел: у него свой ключ,
                  // и владелец может убрать «Дубли», оставив «Клиенток» (`prv3`).
                  const children = (section.children ?? []).filter(
                    (c) => razdelRazreshen(c.permission, can)
                      && (!c.superAdmin || isSuperAdmin)
                      && ! sectionHidden(c.key),
                  );
                  const hasChildren = children.length > 0;
                  const isOpen = isSectionOpen(section.key);
                  const badge = schyotchik(section.key);
                  const showBadge = badge !== null;

                  return (
                    <div key={section.key}>
                      {podgruppaHeader && (
                        <p className="px-3 pb-1 pt-3 text-ops-micro font-semibold uppercase tracking-wider text-ops-sidebar-text-muted">
                          {podgruppaHeader}
                        </p>
                      )}
                      <div className="flex items-stretch gap-0.5">
                        <NavLink
                          to={section.to}
                          end
                          onClick={() => {
                            // `s713`: раскрытый пункт двигает нажатие в меню, а не адрес страницы.
                            raskryt(section.key);
                            onClose?.();
                          }}
                          className={({ isActive }) =>
                            [
                              // py-2.5 на мобильном → тач-таргет ≥44px; md:py-2 — десктоп-плотность.
                              'flex min-w-0 flex-1 items-center gap-2.5 rounded-ops-md px-3 py-2.5 text-ops-label transition-colors duration-ops ease-ops md:py-2',
                              isActive || podKartochkoy(section.to)
                                ? 'bg-ops-sidebar-active-bg font-semibold text-ops-sidebar-active-text'
                                : 'text-ops-sidebar-text hover:bg-white/5 hover:text-white',
                            ].join(' ')
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <Icon
                                size={18}
                                className={['shrink-0 transition-colors', isActive || podKartochkoy(section.to) ? 'text-ops-primary' : 'opacity-90'].join(' ')}
                              />
                              <span className="min-w-0 flex-1 truncate">{section.label}</span>
                              {showBadge ? (
                                <span
                                  data-testid={section.key === 'support' ? 'support-decision-badge' : 'learning-pending-badge'}
                                  className="ml-1 inline-flex min-w-5 items-center justify-center rounded-ops-pill bg-ops-warning px-1.5 text-ops-micro font-bold text-white"
                                >
                                  {badge}
                                </span>
                              ) : null}
                            </>
                          )}
                        </NavLink>

                        {hasChildren ? (
                          <button
                            type="button"
                            onClick={() => toggle(section.key)}
                            aria-label={isOpen ? `Свернуть «${section.label}»` : `Развернуть «${section.label}»`}
                            aria-expanded={isOpen}
                            className="flex w-9 shrink-0 items-center justify-center rounded-ops-md text-ops-sidebar-text-muted transition-colors hover:bg-white/5 hover:text-white"
                          >
                            <ChevronRight
                              size={16}
                              className={['transition-transform duration-200', isOpen ? 'rotate-90' : ''].join(' ')}
                            />
                          </button>
                        ) : null}
                      </div>

                      {hasChildren && isOpen ? (
                        <div className="mb-1 ml-[1.45rem] mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
                          {children.map((leaf) => {
                            /* ⛔ `s329` Д28: метка ставится ТОЛЬКО там, где есть
                               что показать. Ноль метки не рисует — красная точка,
                               горящая всегда, перестаёт значить что-либо (тот же
                               довод, по которому `s324` погасила вечное «Ждём
                               бронь»). «Режим приёмки» метки не имеет вовсе: это
                               переключатель, а не список. */
                            const skolko = metkiZapisi?.[leaf.key] ?? 0;

                            return (
                              <NavLink
                                key={leaf.to}
                                to={leaf.to}
                                onClick={onClose}
                                className={(s) => leafClass({ isActive: s.isActive || podKartochkoy(leaf.to) })}
                              >
                                <span className="min-w-0 flex-1 truncate">{leaf.label}</span>
                                {skolko > 0 && (
                                  <span
                                    data-testid={`metka-${leaf.key}`}
                                    className="ml-1 inline-flex min-w-5 items-center justify-center rounded-ops-pill bg-ops-danger px-1.5 text-ops-micro font-bold text-white"
                                  >
                                    {skolko}
                                  </span>
                                )}
                              </NavLink>
                            );
                          })}
                        </div>
                      ) : null}

                    </div>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Подвал (филиал/профиль/выход) — собирается в AppShell (там провайдеры филиала),
            сюда приходит готовым через проп footer. */}
        {footer && <div className="ops-cosmic-sidebar__footer">{footer}</div>}
        </div>
      </aside>
    </>
  );
}
